import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import usePermissions from 'api/usePermissions';
import useUsers from 'api/useUsers';
import { EPublisherSolutionModelSubType } from 'common/contracts';
import { ELocalStorageKeys, ELogoutMessage } from 'constants/enums';
import { useLogin } from 'hooks/useLogin';
import useUrlQuery from 'hooks/useUrlQuery';
import { authActions } from 'store/authSlice';
import { localStorageUtil } from 'utils/localStorageUtil';
import { permissionsUtil } from 'utils/permissionsUtil';
import { v4 as uuidv4 } from 'uuid';
import { ResetPasswordData } from 'views/Profile/ProfileView.types';

import { EToasterStatus, showToast } from '../../utils/showToast';
import FinishAccountForm from '../FinishAccountForm/FinishAccountForm';
import ForgotPasswordForm from '../ForgotPasswordForm/ForgotPasswordForm';
import LoginForm from '../LoginForm/LoginForm';
import ResetPasswordForm from '../ResetPasswordForm/ResetPasswordForm';

import {
  ELoginAuthMethod,
  LoginData,
  LoginNotificationData,
  LoginPayload,
  ModalState
} from './LoginModal.types';

import 'animate.css';
import './style.scss';

const LoginModal = () => {
  const mode = useUrlQuery('mode') as ModalState;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { saveLoginDetails, isLoggedIn, cleanUserLoginData } = useLogin();
  const {
    forgotPassword,
    resetPassword,
    login,
    loginWithGoogle,
    loginWithMicrosoft,
    loginWithToken
  } = useUsers({});
  const [modalState, setModalState] = useState<ModalState>(mode || 'login');
  const { getPermissions } = usePermissions();
  const setNotificationHandler = (data: LoginNotificationData) => {
    showToast({
      message: data.msg,
      status:
        data.type === 'error' ? EToasterStatus.ERROR : EToasterStatus.SUCCESS
    });
  };

  const setModalStateHandler = (state: ModalState) => {
    setModalState(state);
  };

  const makeLogin = async (
    loginData: LoginData,
    cb: Function,
    loginType: ELoginAuthMethod = ELoginAuthMethod.USERNAME_PASSWORD
  ) => {
    const onLoginSuccess = (loginResponse: any) => {
      const data = loginResponse?.result || loginResponse;
      const loginPayload: LoginPayload = { userId: data.userId };
      if (data?.token) loginPayload.authJwt = data.token;
      dispatch(authActions.login(loginPayload));

      if (!data?.version) {
        showToast({
          message:
            'Seems like you are using an outdated version of the app. Please contact Appcharge support.',
          status: EToasterStatus.ERROR
        });
        cleanUserLoginData(ELogoutMessage.OUTDATED_VERSION);
        cb(false);
        return;
      }

      saveLoginDetails({
        email: data.email,
        firstName: data.userName,
        lastName: '',
        userId: data.userId,
        isSession: !loginData.rememberMe,
        token: data?.token,
        role: data?.role,
        typeId: data?.typeId,
        projects: data?.projects,
        portals: data?.portals,
        version: data?.version,
        company: data?.company,
        companyDisplayName: data?.companyDisplayName,
        isOnboarding: data?.isOnboarding
      });

      cb(true);

      // Handle Zendesk JWT SSO redirect
      if (loginData.isZendesk && loginData.returnTo && data.zendeskToken) {
        const zendeskJwtUrl = `https://appchargehelp.zendesk.com/access/jwt?jwt=${data.zendeskToken}&return_to=${encodeURIComponent(loginData.returnTo)}`;
        console.log('🔄 Attempting Zendesk JWT SSO redirect:', zendeskJwtUrl);

        try {
          window.location.href = zendeskJwtUrl;
          return;
        } catch (error) {
          console.error(
            '❌ Zendesk JWT redirect failed, redirecting to dashboard:',
            error
          );
          // If redirect fails, go to dashboard
          window.location.href = '/dashboard';
          return;
        }
      }

      // If Zendesk login but no token, redirect to dashboard
      if (loginData.isZendesk && loginData.returnTo && !data.zendeskToken) {
        localStorageUtil.set(
          ELocalStorageKeys.ZENDESK_TOKEN,
          data.zendeskToken
        );
        console.warn(
          '⚠️ Zendesk login but no JWT token, redirecting to dashboard'
        );
        window.location.href = '/dashboard';
        return;
      }

      const isOrdersOrPayoutsEnabled =
        permissionsUtil.isAnyOrdersOrPayoutsEnabled(getPermissions());
      const isSingleProject = data?.projects?.length === 1;
      const isPaymentLinkProject =
        data?.projects?.[0]?.projectSubType ===
        EPublisherSolutionModelSubType.PAYMENT_LINKS;

      const getDefaultPath = () => {
        if (isSingleProject && isPaymentLinkProject) {
          const publisherId = data?.projects?.[0]?.publisherId;
          return publisherId ? `/project/${publisherId}/settings` : '';
        }
        if (isOrdersOrPayoutsEnabled) return '/projects';
        return '/projects';
      };

      const defaultPath = getDefaultPath();
      const fromPath = location.state?.from?.pathname;
      const navigationPath =
        fromPath && fromPath !== '/'
          ? `${fromPath}${location.state.from.search || ''}`
          : defaultPath;

      navigate(navigationPath, { replace: true });
    };

    const loginStrategies = {
      [ELoginAuthMethod.MICROSOFT]: loginWithMicrosoft,
      [ELoginAuthMethod.GOOGLE]: loginWithGoogle,
      [ELoginAuthMethod.ACCESS_TOKEN]: loginWithToken,
      [ELoginAuthMethod.USERNAME_PASSWORD]: login
    };

    const strategy = loginStrategies[loginType] || login;
    strategy.mutate(loginData, {
      onSuccess: onLoginSuccess,
      onError: () => {
        cb(false);
      }
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const newSessionId = uuidv4();
    localStorageUtil.set(ELocalStorageKeys.SESSION_ID, newSessionId);
    const token = params.get('t');
    if (token) {
      makeLogin(
        {
          accessToken: token,
          rememberMe: false,
          isZendesk: false
        },
        () => {},
        ELoginAuthMethod.ACCESS_TOKEN
      );
    }
  }, []);

  const sendResetEmail = async (email: string, cb: Function) => {
    forgotPassword.mutate(email, {
      onSuccess: () => {
        setNotificationHandler({
          msg: 'An email has been sent successfully.',
          type: 'success'
        });
        cb();
      },
      onError: (err: any) => {
        setNotificationHandler({
          msg:
            err?.response?.data?.message ||
            'An email has not been sent, please try again.',
          type: 'error'
        });
        cb();
      }
    });
  };

  const setNewPassword = (data: ResetPasswordData, cb: Function) => {
    resetPassword.mutate(data, {
      onSuccess: () => {
        setNotificationHandler({
          msg: 'Password updated successfully.',
          type: 'success'
        });
        cb(true);
      },
      onError: (err) => {
        setNotificationHandler({
          msg:
            (err as any)?.response?.data?.message ||
            'Error in saving password, please try again.',
          type: 'error'
        });
        cb(false);
      }
    });
  };

  useEffect(() => {
    if (isLoggedIn()) {
      let navigationPath = '';

      if (permissionsUtil.isAnyOrdersOrPayoutsEnabled(getPermissions())) {
        navigationPath = '/projects';
      } else {
        navigationPath = '/projects';
      }

      navigate(navigationPath);
    }
  }, [getPermissions, isLoggedIn, navigate]);

  return (
    <div className="w-full">
      {modalState === 'login' ? (
        <LoginForm
          login={makeLogin}
          setLoginNotification={setNotificationHandler}
          setModalState={setModalStateHandler}
        />
      ) : modalState === 'forgetPassword' ? (
        <ForgotPasswordForm
          sendResetEmail={sendResetEmail}
          setModalState={setModalState}
        />
      ) : modalState === 'finishAccount' ? (
        <FinishAccountForm
          setNewPassword={setNewPassword}
          setModalState={setModalState}
        />
      ) : (
        modalState === 'resetPassword' && (
          <ResetPasswordForm
            setNewPassword={setNewPassword}
            setModalState={setModalState}
          />
        )
      )}
    </div>
  );
};

export default LoginModal;
